import type { ApiResponse } from '@personalizehq/types';
import { supabase } from './supabase';

// `??`, NOT `||` — an EMPTY STRING is meaningful here and falsy. It means "same origin": the
// dashboard and its `/api/*` are served from one hostname in production, so the browser sends the
// Cloudflare Access cookie automatically and there is no CORS at all. `|| 'http://localhost:8787'`
// silently turned that into a call to the developer's laptop.
const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';

/** Auth lives at the EDGE (Cloudflare Access) rather than in the app. Set at BUILD time. */
export const ACCESS_MODE = import.meta.env.VITE_AUTH_MODE === 'access';

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async getAuthToken(): Promise<string | null> {
    // Under Access there is no app-level token AND THERE MUST NOT BE ONE: the edge already
    // authenticated this request and hands the Worker a signed `Cf-Access-Jwt-Assertion`. Asking
    // Supabase for a session here would be asking a service we did not configure.
    if (ACCESS_MODE) return null;

    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  }

  private async request<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    const token = await this.getAuthToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options?.headers,
    };

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      return {
        success: false,
        error: {
          // The API's error shape is `{error: string}` (lib/openapi-schemas.ts). Reading only
          // `.message` turned every real failure into "An error occurred" — the 400 that tells you
          // WHICH field is wrong is the whole point of the validation.
          message: error.error || error.message || 'An error occurred',
          code: error.code,
        },
      };
    }

    const data = await response.json();
    return {
      success: true,
      data,
    };
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /** Raw bytes, not JSON — asset upload posts the file straight through the Worker to R2. */
  async postBytes<T>(endpoint: string, bytes: ArrayBuffer, contentType: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: bytes,
      headers: { 'Content-Type': contentType },
    });
  }
}

export const api = new ApiClient(API_URL);
